/**
 * SoleadLab 2.0 - Mobile Upload API Service
 *
 * Frontend API calls for upload link management and field uploads.
 */

import { API_BASE, apiFetch } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UploadLink {
  id: string;
  token: string;
  site_id: string;
  label: string | null;
  is_active: number;
  max_uploads: number | null;
  upload_count: number;
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
}

export interface FieldUpload {
  id: string;
  site_id: string;
  link_id: string | null;
  original_name: string;
  content_type: string;
  r2_key: string;
  file_size: number;
  note: string | null;
  exif_lat: number | null;
  exif_lon: number | null;
  exif_altitude: number | null;
  exif_bearing: number | null;
  exif_capture_date: string | null;
  status: 'pending' | 'placed' | 'archived';
  placed_indication_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface UploadInfo {
  isValid: boolean;
  siteName: string | null;
  label: string | null;
  expiresAt: string | null;
  reason?: 'expired' | 'upload_limit_reached' | 'paused';
}

export interface FieldUploadPage {
  items: FieldUpload[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export function buildAssetUrl(r2Key: string): string {
  return `${API_BASE}/api/v2/assets/${r2Key}`;
}

export function buildUploadPageUrl(token: string): string {
  return `${window.location.origin}/upload/${token}`;
}

// ─── Upload Link Management (Authenticated) ────────────────────────────────

export async function createUploadLink(
  siteId: string,
  opts?: { label?: string; maxUploads?: number; expiresInHours?: number }
): Promise<UploadLink & { uploadUrl: string }> {
  return apiFetch(`/api/v2/sites/${siteId}/upload-links`, {
    method: 'POST',
    body: JSON.stringify(opts ?? {}),
  });
}

export async function listUploadLinks(siteId: string): Promise<UploadLink[]> {
  return apiFetch(`/api/v2/sites/${siteId}/upload-links`);
}

export async function updateUploadLink(
  linkId: string,
  updates: { label?: string; isActive?: boolean }
): Promise<void> {
  await apiFetch(`/api/v2/upload-links/${linkId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteUploadLink(linkId: string): Promise<void> {
  await apiFetch(`/api/v2/upload-links/${linkId}`, {
    method: 'DELETE',
  });
}

// ─── Field Upload Management (Authenticated) ───────────────────────────────

export async function listFieldUploads(
  siteId: string,
  opts?: { page?: number; limit?: number; status?: string }
): Promise<FieldUploadPage> {
  const params = new URLSearchParams();
  if (opts?.page) params.set('page', String(opts.page));
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.status) params.set('status', opts.status);

  const qs = params.toString();
  return apiFetch(
    `/api/v2/sites/${siteId}/uploads${qs ? `?${qs}` : ''}`,
  );
}

export async function updateUploadStatus(
  siteId: string,
  uploadId: string,
  status: 'pending' | 'placed' | 'archived',
  placedIndicationId?: string
): Promise<void> {
  await apiFetch(`/api/v2/sites/${siteId}/uploads/${uploadId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, placedIndicationId }),
  });
}

export async function deleteFieldUpload(
  siteId: string,
  uploadId: string
): Promise<void> {
  await apiFetch(`/api/v2/sites/${siteId}/uploads/${uploadId}`, {
    method: 'DELETE',
  });
}

// ─── Public Endpoints (No Auth) ────────────────────────────────────────────

export async function getUploadInfo(token: string): Promise<UploadInfo> {
  return apiFetch(`/api/v2/public/upload-info/${token}`);
}

export async function publicUpload(
  token: string,
  file: File,
  note?: string,
  lat?: number,
  lon?: number
): Promise<{ id: string; message: string; hasGps: boolean }> {
  const formData = new FormData();
  formData.append('file', file);
  if (note) formData.append('note', note);
  if (lat != null) formData.append('lat', String(lat));
  if (lon != null) formData.append('lon', String(lon));

  return apiFetch(`/api/v2/public/upload/${token}`, {
    method: 'POST',
    body: formData,
  });
}
